/**
 * The interim fight loop — enough combat to prove M1, and no more (AoD §7).
 *
 * **This is not M2-02.** M2 owns real melee: player intents, reach and closing,
 * facing and flanking, the tile grid. What lives here is the smallest honest
 * exchange that can drive ten thousand duels overnight — contested dice, a pool
 * split, wounds through the M1-03 pipeline, clocks through M1-04/05, clauses
 * through M1-06 — because AoD §13's hard-won lesson is that balancing this kind
 * of combat through live play is agony, and the harness has to exist *before*
 * there is anything to balance.
 *
 * What M2 replaces is the **AI**: `allocate` and `chooseStance` stand in for a
 * player's intents. What M2 keeps is everything else, because a contested roll
 * feeding `resolveDamage` is not a placeholder — it is the seam AoD §7.4
 * describes, and the batch runs go on using it long after players arrive.
 *
 * Sides rather than a pair, from the start. The cooperation anchor M1-07 is
 * built to assert — *two farmers with staffs must beat the wolf that kills one
 * farmer* — is not expressible in a duel, and a two-fighter loop that had to
 * grow a third would grow it wrong.
 */
import { createBody, grit, toughnessRating } from './body.js';
import { resolveDamage, weaponDamageRating } from './damage.js';
import { advanceInfection, rollInfections } from './infection.js';
import { armourAt } from './narrate.js';
import { TICKS_PER_GAME_DAY, TICKS_PER_GAME_HOUR } from './time.js';
import { advanceClocksOver, advanceDailyRecovery, bloodOf, BLOOD_THRESHOLDS, canFight, createVitals, effectivePool, isDown, sufferBlow, } from './vitals.js';
import { effectivePain } from './wound.js';
export function createFighter(kit) {
    return {
        kit,
        vitals: createVitals(createBody(kit.plan)),
        stance: 'hold',
        initiative: false,
        fatigue: 0,
        fled: false,
        withdrawing: 0,
    };
}
/* -------------------------------------------------------------------------- *
 * Stance and allocation (AoD §7.3)
 * -------------------------------------------------------------------------- */
export const STANCES = ['press', 'hold', 'fall-back'];
/** Share of the pool that goes to attack. The rest defends. */
export const STANCE_ATTACK_SHARE = {
    press: 0.65,
    hold: 0.5,
    'fall-back': 0.3,
};
/** Exchanges of withdrawal before a fighter is away and out of the fight. */
export const WITHDRAW_EXCHANGES = 3;
/**
 * The interim AI (AoD §7.3): `f(stance order, wounds, odds)`, with the morale
 * and temperament terms left out because M2's band system is where standing
 * comes from and inventing a morale scalar here would be inventing the wrong
 * one.
 *
 * What is kept is the part that matters to balance: **hurt fighters get
 * cautious, and badly hurt fighters leave**. It is why the death rate is a
 * minority of outcomes rather than the whole of them, which is the envelope
 * M1-07 asserts.
 */
export function chooseStance(fighter, odds) {
    const { attributes } = fighter.kit;
    const blood = bloodOf(fighter.vitals);
    const pain = effectivePain(fighter.vitals.wounds, grit(attributes));
    // Below `failing` a fighter cannot fight at all — there is nothing left to
    // decide, only to get away with.
    if (blood < BLOOD_THRESHOLDS.failing)
        return 'fall-back';
    // Nerve is read off the wounds themselves rather than from a morale scalar.
    // M1 has no standing to read from — the band system is M2's — and inventing
    // one here would mean inventing the wrong one and then balancing against it.
    // A wound is observable state, and "how badly am I hurt" is the question a
    // person actually answers before deciding to run.
    const worst = worstWoundLevel(fighter);
    const serious = countWoundsAtLeast(fighter, SERIOUS_WOUND);
    if (worst >= GRIEVOUS_WOUND || serious >= 2)
        return 'fall-back';
    if (worst >= SERIOUS_WOUND && odds <= 1)
        return 'fall-back';
    if (blood < BLOOD_THRESHOLDS.pale && odds <= 1)
        return 'fall-back';
    if (worst >= 2 || pain > attributes.toughness)
        return 'hold';
    // Whole, and in a fight to the death. Pressing is the norm, not the
    // exception — a fighter who never presses never ends anything, and a
    // hundred cagey exchanges is not what a dark-ages killing looks like.
    return 'press';
}
/** A wound at this level makes a fighter think about leaving. */
export const SERIOUS_WOUND = 3;
/** A wound at this level makes the decision for them. */
export const GRIEVOUS_WOUND = 4;
function worstWoundLevel(fighter) {
    let worst = 0;
    for (const wound of fighter.vitals.wounds) {
        if (wound.state === 'healed')
            continue;
        if (wound.level > worst)
            worst = wound.level;
    }
    return worst;
}
function countWoundsAtLeast(fighter, level) {
    let count = 0;
    for (const wound of fighter.vitals.wounds) {
        if (wound.state !== 'healed' && wound.level >= level)
            count += 1;
    }
    return count;
}
/**
 * Split a pool into attack and defence dice.
 *
 * `share` of the pool attacks and the remainder defends, and the rounding
 * favours defence — a fighter down to one die keeps it up rather than throwing
 * it, which is what a person actually does.
 */
export function allocate(pool, stance) {
    const attack = Math.floor(pool * STANCE_ATTACK_SHARE[stance]);
    return { attack: Math.max(0, attack), defence: Math.max(0, pool - attack) };
}
/** The dice a fighter brings this exchange, after wounds, fatigue and armour. */
export function fightingPool(fighter) {
    const { kit } = fighter;
    const encumbrance = kit.armour.reduce((sum, piece) => sum + (piece.poolPenalty ?? 0), 0);
    const base = effectivePool(kit.plan, fighter.vitals, kit.attributes);
    return Math.max(0, base + kit.skill - fighter.fatigue - encumbrance);
}
/* -------------------------------------------------------------------------- *
 * The contested roll (AoD §7.4)
 * -------------------------------------------------------------------------- */
/**
 * A die shows a success on this or better. Ten-sided, as everything else is.
 *
 * Six rather than seven, and the difference is not cosmetic: at even dice the
 * margin distribution is centred on zero either way, but a wider spread is what
 * lets a pressing fighter's extra dice actually *tell*. At seven, two trained
 * men circled each other for sixty exchanges and drew blood four times.
 */
export const SUCCESS_TARGET = 6;
export const COMBAT_DIE = 10;
/** Count successes in `dice` throws. The one place a fight touches the dice. */
export function rollSuccesses(rng, dice) {
    let successes = 0;
    for (let i = 0; i < dice; i += 1) {
        if (rng.roll(COMBAT_DIE) >= SUCCESS_TARGET)
            successes += 1;
    }
    return successes;
}
/**
 * How a losing defender stopped it. Cosmetic in mechanics and load-bearing in
 * the log: AoD §12.1's rule is that the thing that stopped the blow is named,
 * and "the blow misses" is what a log says when nobody modelled the answer.
 */
function defenceKind(rng, defender) {
    if (defender.stance === 'fall-back')
        return 'give-ground';
    return rng.chance(0.5) ? 'parry' : 'dodge';
}
/** Ticks per exchange — AoD §7.1's two real seconds, on Annwn's tick rate. */
export const TICKS_PER_EXCHANGE = 8;
/** Fatigue costs a die every this many exchanges (AoD §7.5). */
export const EXCHANGES_PER_FATIGUE = 4;
export const DEFAULT_MAX_EXCHANGES = 60;
export const FIGHT_ENDINGS = ['victory', 'mutual-collapse', 'stalemate'];
/**
 * Still in it: alive, upright, present, and with dice left to throw.
 *
 * That last clause is what AoD §7.6 means by *incapacitation* — the ending it
 * expects to be commoner than death. A fighter whose pool has been eaten by
 * pain, blood and fatigue is not unconscious; they are finished, and pretending
 * otherwise is how a fight runs to the exchange cap with nobody left able to
 * hurt anybody.
 */
export function isFighting(fighter, tick) {
    if (fighter.fled || fighter.vitals.dead)
        return false;
    if (isDown(fighter.kit.plan, fighter.vitals, tick))
        return false;
    return fightingPool(fighter) > 0;
}
/**
 * Run a fight to its end.
 *
 * The order of everything here is fixed and load-bearing, because it is what a
 * replay reproduces: fighters act in initiative order and then id order, each
 * attack draws attack dice then defence dice then the pipeline's rolls, and the
 * clocks run once at the end of the exchange. A roll inserted anywhere ahead of
 * another shifts every subsequent draw of every archived battle.
 */
export function runFight(options) {
    const { rng, fighters, table } = options;
    const maxExchanges = options.maxExchanges ?? DEFAULT_MAX_EXCHANGES;
    let tick = options.startTick ?? 0;
    const exchanges = [];
    let woundSeq = 0;
    for (let index = 0; index < maxExchanges; index += 1) {
        const standing = fighters.filter((fighter) => isFighting(fighter, tick));
        const sides = new Set(standing.map((fighter) => fighter.kit.side));
        if (sides.size < 2) {
            return finish(exchanges, standing, tick);
        }
        for (const fighter of standing) {
            const enemies = standing.filter((other) => other.kit.side !== fighter.kit.side).length;
            const allies = standing.length - enemies;
            fighter.stance = chooseStance(fighter, allies / Math.max(1, enemies));
        }
        const blows = [];
        for (const attacker of order(standing)) {
            if (!isFighting(attacker, tick))
                continue;
            if (attacker.vitals.staggered) {
                // Shock cost them the action (AoD §5.1). The stagger is spent, not free.
                attacker.vitals.staggered = false;
                continue;
            }
            if (attacker.stance === 'fall-back') {
                attacker.withdrawing += 1;
                if (attacker.withdrawing >= WITHDRAW_EXCHANGES)
                    attacker.fled = true;
                // Withdrawing is not attacking, but the dice still defend.
                continue;
            }
            attacker.withdrawing = 0;
            const target = pickTarget(rng, standing, attacker, tick);
            if (!target)
                continue;
            woundSeq += 1;
            blows.push(resolveExchange({
                rng,
                table,
                attacker,
                defender: target,
                standing,
                tick,
                woundId: `${options.battleId}-w${woundSeq}`,
                battleId: options.battleId,
                where: options.where,
            }));
        }
        exchanges.push({ index, tick, blows });
        for (const fighter of fighters) {
            if (index > 0 && index % EXCHANGES_PER_FATIGUE === 0) {
                const stamina = Math.round(fighter.kit.attributes.toughness / 10);
                fighter.fatigue += Math.max(1, 2 - stamina);
            }
            advanceClocksOver(fighter.vitals, tick + 1, TICKS_PER_EXCHANGE);
        }
        tick += TICKS_PER_EXCHANGE;
    }
    return finish(exchanges, fighters.filter((fighter) => isFighting(fighter, tick)), tick, true);
}
function finish(exchanges, standing, tick, exhausted = false) {
    const sides = new Set(standing.map((fighter) => fighter.kit.side));
    if (exhausted && sides.size > 1) {
        return { exchanges, ending: 'stalemate', winner: null, endTick: tick };
    }
    if (sides.size === 1) {
        return { exchanges, ending: 'victory', winner: [...sides][0], endTick: tick };
    }
    return { exchanges, ending: 'mutual-collapse', winner: null, endTick: tick };
}
/** Initiative first, then by id — a total order, so a replay cannot reorder it. */
function order(standing) {
    return [...standing].sort((a, b) => {
        if (a.initiative !== b.initiative)
            return a.initiative ? -1 : 1;
        return a.kit.id < b.kit.id ? -1 : a.kit.id > b.kit.id ? 1 : 0;
    });
}
/**
 * Who to swing at. The weakest enemy still standing, ties broken by the dice —
 * a wolf takes the one already bleeding, which is both what a wolf does and
 * what makes ganging up worth anything.
 */
function pickTarget(rng, standing, attacker, tick) {
    const enemies = standing.filter((other) => other.kit.side !== attacker.kit.side && isFighting(other, tick));
    if (enemies.length === 0)
        return null;
    if (enemies.length === 1)
        return enemies[0];
    let weakest = Number.POSITIVE_INFINITY;
    for (const enemy of enemies)
        weakest = Math.min(weakest, bloodOf(enemy.vitals));
    const hurt = enemies.filter((enemy) => bloodOf(enemy.vitals) === weakest);
    return rng.pick(hurt);
}
/**
 * One attack: contested dice, then — if the attack won — the whole M1-03/04/05
 * pipeline, reported as the tuple M1-06 renders.
 *
 * The defender's dice are split across everyone swinging at them this exchange
 * (AoD §7.1's multiple-opponents rule). That single division is what makes two
 * farmers beat a wolf that kills one of them: the wolf cannot parry both, and
 * cooperation buys more than gear does.
 */
function resolveExchange(options) {
    const { rng, attacker, defender, tick } = options;
    const attackerAlloc = allocate(fightingPool(attacker), attacker.stance);
    const defenderAlloc = allocate(fightingPool(defender), defender.stance);
    const pressing = options.standing.filter((other) => other.kit.side !== defender.kit.side && isFighting(other, tick)).length;
    const defenceDice = Math.floor(defenderAlloc.defence / Math.max(1, pressing));
    const attackRoll = rollSuccesses(rng, attackerAlloc.attack);
    const defenceRoll = rollSuccesses(rng, defenceDice);
    const margin = attackRoll - defenceRoll;
    const parties = {
        attacker: party(attacker),
        defender: party(defender),
        weapon: attacker.kit.weapon.name,
        attack: attacker.kit.profile.id,
    };
    if (margin <= 0) {
        // A clean defence buys the riposte rhythm of AoD §7.4 — one bit of state.
        defender.initiative = margin < 0;
        attacker.initiative = false;
        return {
            ...parties,
            defence: defenceKind(rng, defender),
            armour: null,
            damage: null,
            events: [],
        };
    }
    attacker.initiative = true;
    defender.initiative = false;
    const provenance = {
        cause: attacker.kit.plan.id === 'human' ? 'melee' : 'beast',
        battleId: options.battleId,
        attacker: { id: attacker.kit.id, name: attacker.kit.name },
        weapon: attacker.kit.weapon.name,
        where: options.where,
        narrative: `${attacker.kit.name} struck ${defender.kit.name}.`,
    };
    const damage = resolveDamage({
        rng,
        plan: defender.kit.plan,
        body: defender.vitals.body,
        wounds: defender.vitals.wounds,
        table: options.table,
        profile: attacker.kit.profile,
        margin,
        damageRating: weaponDamageRating(attacker.kit.weapon, attacker.kit.attributes),
        toughness: toughnessOf(defender),
        armour: defender.kit.armour,
        tick,
        woundId: options.woundId,
        provenance,
    });
    // Refill before shock lands so the stagger test is against a real exchange's
    // dice rather than whatever was left of the last one.
    defender.vitals.pool = defenderAlloc.defence;
    const events = sufferBlow(rng, defender.kit.plan, defender.vitals, defender.kit.attributes, damage, tick);
    // Named whether it held or not — what turned the blow, or what the blow went
    // through on its way in (M2-04).
    const covering = armourAt(defender.kit.armour, damage.zone, attacker.kit.profile.damageType);
    return {
        ...parties,
        defence: damage.turnedByArmour ? 'armour' : null,
        armour: covering?.name ?? null,
        damage,
        events,
    };
}
function party(fighter) {
    return { id: fighter.kit.id, name: fighter.kit.name, pronouns: fighter.kit.pronouns };
}
function toughnessOf(fighter) {
    return toughnessRating(fighter.kit.attributes);
}
/**
 * Fast-forward the clocks past the fight — the `--fast-forward 72h` mode, and
 * the only way to see what M1-04 and M1-05 actually do to a survivor.
 *
 * This is where the milestone's whole argument lands: the fight is decided in a
 * minute, and then the bleeding, the fever and the gangrene take the week that
 * decides who is still alive at the end of it.
 */
export function runAftermath(options) {
    const { rng, fighters } = options;
    const log = [];
    for (let hour = 0; hour < options.hours; hour += 1) {
        const tick = options.startTick + hour * TICKS_PER_GAME_HOUR;
        for (const fighter of fighters) {
            const record = (events) => {
                for (const event of events)
                    log.push({ fighter: fighter.kit.id, tick, event });
            };
            record(advanceClocksOver(fighter.vitals, tick, TICKS_PER_GAME_HOUR));
            record(rollInfections(rng, fighter.vitals, options.environment));
            if ((tick + TICKS_PER_GAME_HOUR) % TICKS_PER_GAME_DAY === 0) {
                record(advanceDailyRecovery(fighter.vitals));
                record(advanceInfection(rng, fighter.vitals, fighter.kit.attributes, options.care ?? 0));
            }
        }
    }
    return log;
}
/** Did they walk away? The question the aftermath actually answers. */
export function survived(fighter, tick) {
    return !fighter.vitals.dead && canFight(fighter.kit.plan, fighter.vitals, tick);
}
//# sourceMappingURL=exchange.js.map